import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconCheck,
  IconGripVertical,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';

import LlmConfigHistory from './LlmConfigHistory';
import {
  type LlmConfigItem,
  type ReasoningEffort,
  useLlmConfig,
  useResetLlmConfig,
  useUpdateLlmConfig,
} from './hooks/useLlmConfig';

const REASONING_EFFORT_OPTIONS: ReasoningEffort[] = [
  'none',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
];

type FallbackModel = {
  name: string;
  provider: string;
  'max-input-tokens'?: number;
  'reasoning-effort'?: ReasoningEffort;
};

function parseFallbackModels(modelName: string): FallbackModel[] {
  try {
    return JSON.parse(modelName);
  } catch {
    return [];
  }
}

export default function LlmConfigTab() {
  const { configs, isLoading, refetch } = useLlmConfig();
  const [editingSlot, setEditingSlot] = useState<string | null>(null);

  if (isLoading) {
    return <PageLoader />;
  }

  const singleModelConfigs =
    configs?.filter(c => !c.slotKey.startsWith('llm-fallback-routes.')) ?? [];
  const fallbackRouteConfigs =
    configs?.filter(c => c.slotKey.startsWith('llm-fallback-routes.')) ?? [];

  return (
    <Stack gap="lg">
      <Box>
        <Text fw={600} size="lg">
          LLM Model Configuration
        </Text>
        <Text size="sm" c="dimmed">
          Override model configurations without redeploying. Changes take effect within 60 seconds.
        </Text>
      </Box>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Slot</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Reasoning</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {singleModelConfigs.map(config => (
            <SingleModelRow
              key={config.slotKey}
              config={config}
              isEditing={editingSlot === config.slotKey}
              onEdit={() => setEditingSlot(config.slotKey)}
              onCancel={() => setEditingSlot(null)}
              onSaved={() => {
                setEditingSlot(null);
                refetch();
              }}
            />
          ))}
        </Table.Tbody>
      </Table>

      {fallbackRouteConfigs.length > 0 && (
        <Stack gap="md">
          <Box>
            <Text fw={600} size="md">
              Fallback Routes
            </Text>
            <Text size="sm" c="dimmed">
              Ordered list of models to try. If the first model fails, the next is used.
            </Text>
          </Box>
          {fallbackRouteConfigs.map(config => (
            <FallbackRouteCard key={config.slotKey} config={config} onSaved={refetch} />
          ))}
        </Stack>
      )}

      <LlmConfigHistory />
    </Stack>
  );
}

function SingleModelRow({
  config,
  isEditing,
  onEdit,
  onCancel,
  onSaved,
}: {
  config: LlmConfigItem;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [modelName, setModelName] = useState(config.modelName);
  const [provider, setProvider] = useState(config.provider);
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort | null>(
    config.reasoningEffort,
  );
  const { updateConfig, isLoading: isUpdating } = useUpdateLlmConfig();
  const { resetConfig, isLoading: isResetting } = useResetLlmConfig();

  const handleSave = async () => {
    await updateConfig(config.slotKey, modelName, provider, reasoningEffort);
    onSaved();
  };

  const handleReset = async () => {
    await resetConfig(config.slotKey);
    onSaved();
  };

  const handleStartEdit = () => {
    setModelName(config.modelName);
    setProvider(config.provider);
    setReasoningEffort(config.reasoningEffort);
    onEdit();
  };

  if (isEditing) {
    return (
      <Table.Tr>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {config.slotKey}
          </Text>
        </Table.Td>
        <Table.Td>
          <TextInput
            size="xs"
            value={modelName}
            onChange={e => setModelName(e.currentTarget.value)}
          />
        </Table.Td>
        <Table.Td>
          <TextInput
            size="xs"
            value={provider}
            onChange={e => setProvider(e.currentTarget.value)}
          />
        </Table.Td>
        <Table.Td>
          <Select
            size="xs"
            clearable
            placeholder="provider default"
            data={REASONING_EFFORT_OPTIONS}
            value={reasoningEffort}
            onChange={value => setReasoningEffort(value as ReasoningEffort | null)}
          />
        </Table.Td>
        <Table.Td />
        <Table.Td>
          <Group gap="xs">
            <ActionIcon
              variant="filled"
              color="green"
              size="sm"
              onClick={handleSave}
              loading={isUpdating}
            >
              <IconCheck size={14} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onCancel}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {config.slotKey}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{config.modelName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{config.provider}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{config.reasoningEffort ?? '-'}</Text>
      </Table.Td>
      <Table.Td>
        {config.isOverridden ? (
          <Badge color="blue" size="sm">
            Overridden
          </Badge>
        ) : (
          <Badge color="gray" size="sm" variant="light">
            Default
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon variant="subtle" size="sm" onClick={handleStartEdit}>
            <IconPencil size={14} />
          </ActionIcon>
          {config.isOverridden && (
            <ActionIcon
              variant="subtle"
              color="orange"
              size="sm"
              onClick={handleReset}
              loading={isResetting}
            >
              <IconArrowBackUp size={14} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

type EditRow = { rowId: string; model: FallbackModel };

function FallbackRouteCard({ config, onSaved }: { config: LlmConfigItem; onSaved: () => void }) {
  const modality = config.slotKey.replace('llm-fallback-routes.', '');
  const currentModels = parseFallbackModels(config.modelName);

  const [isEditing, setIsEditing] = useState(false);
  const [editRows, setEditRows] = useState<EditRow[]>([]);
  const { updateConfig, isLoading: isUpdating } = useUpdateLlmConfig();
  const { resetConfig, isLoading: isResetting } = useResetLlmConfig();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleStartEdit = () => {
    setEditRows(currentModels.map(m => ({ rowId: crypto.randomUUID(), model: { ...m } })));
    setIsEditing(true);
  };

  const handleSave = async () => {
    const serialized = JSON.stringify(editRows.map(r => r.model));
    await updateConfig(config.slotKey, serialized, '__list__');
    setIsEditing(false);
    onSaved();
  };

  const handleReset = async () => {
    await resetConfig(config.slotKey);
    setIsEditing(false);
    onSaved();
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = editRows.findIndex(r => r.rowId === active.id);
    const newIndex = editRows.findIndex(r => r.rowId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setEditRows(arrayMove(editRows, oldIndex, newIndex));
  };

  const removeRow = (rowId: string) => {
    setEditRows(editRows.filter(r => r.rowId !== rowId));
  };

  const addRow = () => {
    setEditRows([...editRows, { rowId: crypto.randomUUID(), model: { name: '', provider: '' } }]);
  };

  const updateRow = (rowId: string, field: 'name' | 'provider', value: string) => {
    setEditRows(
      editRows.map(r => (r.rowId === rowId ? { ...r, model: { ...r.model, [field]: value } } : r)),
    );
  };

  const updateRowEffort = (rowId: string, value: ReasoningEffort | null) => {
    setEditRows(
      editRows.map(r => {
        if (r.rowId !== rowId) return r;
        const { 'reasoning-effort': _removed, ...rest } = r.model;
        return { ...r, model: value ? { ...rest, 'reasoning-effort': value } : rest };
      }),
    );
  };

  return (
    <Card withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <Text fw={600} size="sm" tt="capitalize">
            {modality}
          </Text>
          {config.isOverridden ? (
            <Badge color="blue" size="sm">
              Overridden
            </Badge>
          ) : (
            <Badge color="gray" size="sm" variant="light">
              Default
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {!isEditing ? (
            <>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                onClick={handleStartEdit}
              >
                Edit
              </Button>
              {config.isOverridden && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="orange"
                  leftSection={<IconArrowBackUp size={14} />}
                  onClick={handleReset}
                  loading={isResetting}
                >
                  Reset
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="filled"
                size="xs"
                color="green"
                onClick={handleSave}
                loading={isUpdating}
              >
                Save
              </Button>
              <Button variant="subtle" size="xs" color="gray" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </Group>
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            {isEditing && <Table.Th w={32} />}
            <Table.Th w={50}>#</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Reasoning</Table.Th>
            {isEditing && <Table.Th w={48} />}
          </Table.Tr>
        </Table.Thead>
        {isEditing ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={editRows.map(r => r.rowId)}
              strategy={verticalListSortingStrategy}
            >
              <Table.Tbody>
                {editRows.map((row, index) => (
                  <SortableRouteRow
                    key={row.rowId}
                    row={row}
                    index={index}
                    onFieldChange={(field, value) => updateRow(row.rowId, field, value)}
                    onEffortChange={value => updateRowEffort(row.rowId, value)}
                    onRemove={() => removeRow(row.rowId)}
                  />
                ))}
              </Table.Tbody>
            </SortableContext>
          </DndContext>
        ) : (
          <Table.Tbody>
            {currentModels.map((model, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {index + 1}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{model.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{model.provider}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{model['reasoning-effort'] ?? '-'}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        )}
      </Table>

      {isEditing && (
        <Button
          variant="subtle"
          size="xs"
          mt="xs"
          leftSection={<IconPlus size={14} />}
          onClick={addRow}
        >
          Add model
        </Button>
      )}
    </Card>
  );
}

function SortableRouteRow({
  row,
  index,
  onFieldChange,
  onEffortChange,
  onRemove,
}: {
  row: EditRow;
  index: number;
  onFieldChange: (field: 'name' | 'provider', value: string) => void;
  onEffortChange: (value: ReasoningEffort | null) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.rowId,
  });

  return (
    <Table.Tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <Table.Td>
        <IconGripVertical
          size={16}
          style={{ cursor: 'grab', color: 'var(--mantine-color-gray-5)' }}
          {...attributes}
          {...listeners}
        />
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {index + 1}
        </Text>
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          value={row.model.name}
          onChange={e => onFieldChange('name', e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          value={row.model.provider}
          onChange={e => onFieldChange('provider', e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          clearable
          placeholder="provider default"
          data={REASONING_EFFORT_OPTIONS}
          value={row.model['reasoning-effort'] ?? null}
          onChange={value => onEffortChange(value as ReasoningEffort | null)}
        />
      </Table.Td>
      <Table.Td>
        <ActionIcon variant="subtle" color="red" size="sm" onClick={onRemove}>
          <IconTrash size={14} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}
